import { Assignment, Class, FlashcardSet, Folder, ItemType, Media, Note } from "@knowt/syncing/graphql/schema";
import { isAudio, isExcel, isPDF, isPPT, isVideo } from "@knowt/syncing/hooks/media/checkers";
import { UNTITLED, isAssignment, isFlashcardSet, isFolder, isMedia, isNote } from "@knowt/syncing/utils/dataCleaning";
import { UserContentItem, UserContentItemType, UserContentSortBy, UserContentSortSettings } from "./types";
import { SortOrder } from "@/components/styled/table/types";

export const sortUserContentItems = (items: UserContentItem[] | Class[], sort: UserContentSortSettings) => {
    const isAscending = sort.direction === SortOrder.ASC;

    return items.sort((card1, card2) => {
        const {
            created: created1,
            updated: updated1,
            addedAt: addedAt1,
        } = card1 as { created: number | string; updated: number | string; addedAt: string };

        const {
            created: created2,
            updated: updated2,
            addedAt: addedAt2,
        } = card2 as { created: number | string; updated: number | string; addedAt: string };

        const title1 = (card1 as FlashcardSet | Note | Media | Assignment)?.title || (card1 as Class)?.name || UNTITLED;
        const title2 = (card2 as FlashcardSet | Note | Media | Assignment)?.title || (card2 as Class)?.name || UNTITLED;

        switch (sort.by) {
            case UserContentSortBy.CREATED:
                return (Number(created1) - Number(created2)) * (isAscending ? 1 : -1);
            case UserContentSortBy.NAME:
                return title1.localeCompare(title2, undefined, { numeric: true }) * (isAscending ? 1 : -1);
            case UserContentSortBy.ADDED:
                return (Number(addedAt1) - Number(addedAt2)) * (isAscending ? 1 : -1);
            default:
                return (Number(updated1) - Number(updated2)) * (isAscending ? 1 : -1);
        }
    });
};

export const sortFoldersToTop = (items: UserContentItem[]) => {
    const folders: UserContentItem[] = [];
    const otherItems: UserContentItem[] = [];

    for (const item of items) {
        if (isFolder(item)) {
            folders.push(item);
        } else {
            otherItems.push(item);
        }
    }

    return [...folders, ...otherItems];
};
/** _pinned_ is bookmarked item by owner */
export const sortPinnedItemsToTop = (
    items: UserContentItem[],
    pinnedIds: string[],
    bookmarkedIds?: string[]
): UserContentItem[] => {
    const pinnedItems: UserContentItem[] = [];
    const bookmarkedItems: UserContentItem[] = [];
    const otherItems: UserContentItem[] = [];

    for (const item of items) {
        const id = isNote(item)
            ? (item as Note).noteId
            : isFlashcardSet(item)
              ? (item as FlashcardSet).flashcardSetId
              : isAssignment(item)
                ? (item as Assignment).assignmentId
                : (item as Media).mediaId;

        if (pinnedIds.includes(id as string)) {
            pinnedItems.push(item);
        } else if (bookmarkedIds?.includes(id as string)) {
            bookmarkedItems.push(item);
        } else {
            otherItems.push(item);
        }
    }

    return [...pinnedItems, ...bookmarkedItems, ...otherItems];
};

export const filterUserContentItems = (items: UserContentItem[], itemTypesToShow: UserContentItemType[]) => {
    if (!items.length) return [];

    const typesToShowSet = new Set(itemTypesToShow);
    const showAll = itemTypesToShow.length === 0;
    const savedOnly = typesToShowSet.has("PINNED_ONLY");

    return items.filter(item => {
        if (savedOnly && !item.isBookmarked) return false;

        if (showAll) return true;

        if (isFolder(item)) return typesToShowSet.has("FOLDER");
        if (isNote(item)) return typesToShowSet.has("NOTE");
        if (isFlashcardSet(item)) return typesToShowSet.has("FLASHCARDSET");
        if (isAssignment(item)) return typesToShowSet.has("ASSIGNMENT");
        if (isMedia(item)) {
            return (
                (isVideo(item) && typesToShowSet.has("VIDEO")) ||
                (isAudio(item) && typesToShowSet.has("AUDIO")) ||
                (isPDF(item) && typesToShowSet.has("PDF")) ||
                (isPPT(item) && typesToShowSet.has("POWERPOINT")) ||
                (isExcel(item) && typesToShowSet.has("EXCEL"))
            );
        }

        return false;
    });
};

export const sortHomeFolder = (folders: Folder[], sort: UserContentSortSettings) => {
    const isAscending = sort.direction === SortOrder.ASC;

    return folders.sort((folder1, folder2) => {
        const { created: created1, updated: updated1 } = folder1;
        const { created: created2, updated: updated2 } = folder2;
        const title1 = folder1.name ?? UNTITLED;
        const title2 = folder2.name ?? UNTITLED;

        switch (sort.by) {
            case UserContentSortBy.CREATED:
                return (Number(created1) - Number(created2)) * (isAscending ? 1 : -1);
            case UserContentSortBy.NAME:
                return title1.localeCompare(title2, undefined, { numeric: true }) * (isAscending ? 1 : -1);
            default:
                return (Number(updated1) - Number(updated2)) * (isAscending ? 1 : -1);
        }
    });
};

export const getNameFromItemType = (type: ItemType) => {
    if (type === ItemType.NOTE) {
        return "note";
    } else if (type === ItemType.FLASHCARDSET) {
        return "flashcard set";
    } else if (type === ItemType.MEDIA) {
        return "media";
    } else if (type === ItemType.FOLDER) {
        return "folder";
    } else if (type === ItemType.CLASS) {
        return "class";
    } else if (type === ItemType.ASSIGNMENT) {
        return "assignment";
    }

    throw new Error("type is out of what our conditional check");
};
